import {Component, OnInit, AfterViewInit, ChangeDetectorRef, Renderer2, HostListener} from '@angular/core';
import {FormGroup, FormControl} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {Donation, DonatorData} from "./donation";
import { OptionService } from "../option/option.service";
import { DonationService } from "./donation.service";
import { Contact } from "../contact/contact";
import { StatusComponent } from "../output/output.status.component";
import {TrackingComponent} from "../tracking/tracking.component";
import {RythmTargetComponent} from "../rythm_target/rythm_target.component";
import {PaymentComponent} from "../payment/payment.component";
import {ContactComponent} from "../contact/contact.component";
import {UrlService} from "../url/url.service";
import {HttpClient} from "@angular/common/http";
import {DecimalPipe} from "@angular/common";
import {ElementService} from "../service/element.service";
import {StripeService} from "../payment/payment.stripe.service";

declare let window: any;
declare let __customFormAction: any;
declare let __twingle_trigger: any;
declare let __twingle_doneTransaction: any;
declare let __twingle_doneTransactionFinished: any;
declare let attachEvent: any;

@Component({
    selector: 'donation-app',
    templateUrl: './donation.component.html',
    providers: [OptionService, DonationService, UrlService, DecimalPipe]
})
export class DonationComponent implements OnInit, AfterViewInit {
    /**
     * configure the retry times and count for the token.
     */
    static readonly TOKEN_RETRY_COUNT = 3;
    static readonly TOKEN_RETRY_TIMEOUT = 2000;

    /**
     * Holds the users input of the form
     */
    donation: Donation = new Donation();

    /**
     * Holds the contactData
     * @type {ContactData}
     */
    contact: Contact = new Contact();

    /**
     * holds the version of the output (widget|page|form)
     */
    version: string;

    /**
     * holds the current step of the output
     * @type {string}
     */
    step: string = "start";

    /**
     * The size that is available while using the application
     * @type {string}
     */
    size: string = "small";

    /**
     * Holds the status of a donatin process to show errors or warnings
     *
     * @type {null}
     */
    donationStatus: string = StatusComponent.OK;

    /**
     * Holds a specific status message in case of problems with the donation
     *
     * @type {null}
     */
    donationStatusMessage: any = null;

    /**
     * remember if the donation token was already used.
     */
    donationTokenUsed = false;

    /**
     * Form for all inputs on the page
     */
    pageForm: FormGroup;

    /**
     * Form for all inputs on the widget on the start step
     */
    widgetStartForm: FormGroup;

    /**
     * Form for all inputs on the widget on the payment step
     */
    widgetPaymentForm: FormGroup;

    /**
     * Form for the contact data
     */
    contactForm: FormGroup;

    /**
     * Holds the current bodyheight
     *
     * @type {number}
     */
    bodyHeight = 0;

    /**
     * Standard form action for the forms in the application
     * @type {String}
     */
    formAction: string = null;

    /**
     * Selector for counting a visit of the widget
     * @type {string}
     */
    static VISIT_SELECTOR = "label, input, button, .slider-handle";

    /**
     * Holds if the contact data is requested mandatory
     * @type {boolean}
     */
    contactMandatory: boolean = false;

    /**
     * toggle for showing/hiding the widget headline
     * @type {boolean}
     */
    showWidgetHeadline: boolean = true;

    /**
     * holds the type of the project. If set to null, this is a standard project.
     * Other posible values: event
     */
    projectType: string = null;

    /**
     * Should the stepindex be visible instead the defaull navigation?
     * @type {boolean}
     */
    hasStepIndex: boolean = false;

    /**
     * Textkey for the last submit Button
     */
    paymentSubmitTextkey: string = 'payment.submit.now';

    /**
     * Toggle for showing the fields for the donator data
     */
    showDonatorData: boolean = false;

    /**
     * toggle for hiding the donation widget. i.e. can be used on event donations that are stopped
     */
    hideDonationWidget: boolean = false;

    /**
     * Force the header data (headline, teaser) also on the widget and form variant.
     */
    forceWidgetTeaser = false;

    /**
     * Toggle to sho an extra thankyou message, instead of the defaukt header
     */
    showPageheaderRedirectText = false;

    /**
     * set the target of the payment forms. Imprtant for redirect payment methods.
     */
    paymentFormTarget: string = "external-payment";

    /**
     * The available screensizes we want to check for
     */
    screenSizes: any = {
        small: 0,
        medium: 580,
        large: 768
    }

    /**
     * Timeout for checking the redirect status. IF X seconds after starting a redirect transation the status has not changed, we should react here.
     */
    public static readonly CHECK_REDIRECT_STATUS_SECONDS: number = 5;

    /**
     * The constructor
     * @param optionService
     * @param translate
     * @param donationService
     * @param http
     * @param changeDetector We need this detector to manuell trigger the change detection of the app
     * @param urlService
     * @param renderer
     * @param decimalPipe
     * @param elementService
     * @param stripeService
     */
    constructor(protected optionService: OptionService,
                protected translate: TranslateService,
                protected donationService: DonationService,
                protected http: HttpClient,
                protected changeDetector: ChangeDetectorRef,
                protected urlService: UrlService,
                protected renderer: Renderer2,
                protected decimalPipe: DecimalPipe,
                protected elementService: ElementService,
                protected stripeService: StripeService) {
        // set the version (widget, page, form)
        this.version = this.optionService.getType();

        // set the projecttype
        this.projectType = this.optionService.getProjectType();
        if(this.projectType === 'membership') {
            this.paymentSubmitTextkey = 'payment.submit.now.membership';
        }

        this.initTranslator();

        // set the action for the form
        this.formAction = OptionService.getTransactionApiHost() + DonationService.TRANSACTION_START;

        this.initDonation();
    }

    /**
     * Initialize the translator, based on the languages that are available in this project
     */
    initTranslator(): void {
        // get available languages
        const availableLanguages = this.optionService.getOption("languages");

        // Use english as fallback language. If en is not activated, use the first activated language as fallback.
        this.translate.setDefaultLang(availableLanguages.indexOf("de") === -1 ? availableLanguages[0] : "de");

        // Use the current browser language. If the language is not active in the project, use English.
        let languageToUse: string = this.translate.getBrowserLang();
        if (availableLanguages.indexOf(this.translate.getBrowserLang()) === -1) {
            languageToUse = "en";
        }

        // Use the first ative language if the currently selected is not available.
        const language = availableLanguages.indexOf(languageToUse) === -1 ? availableLanguages[0] : languageToUse;
        this.translate.use(language);

        // Set the language on the HTML element.
        this.renderer.setAttribute(document.documentElement, 'lang', language);
    }

    /**
     * Eventhandler for stepChanges in child components
     * @param newStep
     * @param scroll
     */
    stepChange(newStep: string, scroll = false): void {
        if(newStep == "contact-mandatory-finished") {
            // special case, the contact data was saved, lets decide were to go next
            if(this.size == "small" || this.version == "widget") {
                // next step: payment
                newStep = "payment-select";
            }
            else {
                // on medium and large, start the validation directly
                this.startDonation();
                return;
            }
        }
        this.setStep(newStep, scroll);
        this.changeDetector.detectChanges();
    }

    /**
     * Returns the action for the forms in the application
     * @returns {any}
     */
    getFormAction(): string {
        if(typeof __customFormAction == "function") {
            let formAction = __customFormAction(this.optionService.getType(), this.size, this.step, this.donation);
            if(formAction) {
                return formAction;
            }
        }

        return this.formAction;
    }

    /**
     * Eventhandler that is called when the window is resized
     * @param event
     */
    @HostListener('window:resize', ['$event'])
    resizeWindow(event: any = null): void {
        /**
         * 0-579 => small
         * 580-767 => medium
         * 768-? => large
         */
        let currentSize: string;
        for (const size of Object.keys(this.screenSizes)) {
            if (window.innerWidth >= this.screenSizes[size]) {
                currentSize = size;
            }
        }

        if (Object.keys(this.screenSizes).indexOf(currentSize) === -1) {
            currentSize = Object.keys(this.screenSizes)[Object.keys(this.screenSizes).length - 1];
        }

        if (this.size !== currentSize) {
            // viewport has changed
            this.size = currentSize;

            // fire changes detection manually, other wise the button does not knwo what to do
            this.changeDetector.detectChanges();

            // Special case: if started on small and be on step "payment"
            if (this.size !== "small" && this.step === "payment-select") {
                // back to the start step
                if (this.projectType === "shop" || this.projectType === "giftshop") {
                    this.setStep("payment");
                }
                else {
                    this.setStep("start");
                }
                this.changeDetector.detectChanges();
            }

            // special case. On small screens in page version we have a payment step. On medium/large not.
            if(this.version === "page" && this.size !== "small" && this.step === "payment") {
                // back to the start step
                this.setStep("start");
                this.changeDetector.detectChanges();
            }
        }
    }

    /**
     * Initialize the donation with some presets
     */
    initDonation(): void {
        if(typeof __twingle_doneTransaction !== "undefined") {
            this.donation.copyData(__twingle_doneTransaction);

            // set donation status
            const event = {
                data: {
                    result: __twingle_doneTransactionFinished === true? 1 : 2,
                    message: ""
                }
            };
            this.messageReceiver(event);

            if(this.isEmbedded()) {
                // send to the parent frame to scroll upwards
                parent.postMessage({
                    "type": "scrollToTop",
                    "value": 0,
                    "cid": OptionService.getConatinerId()
                }, "*");
            }

            return;
        }

        // curreny + symbol
        this.donation.currency = this.optionService.getOrganisationData("currency");
        switch(this.donation.currency) {
            case "EUR":
                this.donation.currencySymbol = "€";
                break;
            default:
                this.donation.currencySymbol = this.donation.currency;
        }

        // set the donation type to event if this is a event project
        if (this.projectType === 'event') {
            this.donation.transactionType = Donation.TRANSACTION_TYPE_EVENT;
            this.donation.eventId = this.optionService.getEventOption('id');
            this.donation.eventInternalId = this.optionService.getEventOption('internal_id');

            // check if this event is already overdued
            const endsAt = this.optionService.getEventOption('ends_at');
            if(endsAt && (endsAt * 1 + 86400) < (Date.now() / 1000)) {
                this.hideDonationWidget = true;
            }
        }
        else if (this.optionService.getTransactionType()?.length > 0) {
            // set thtransactiontype based on the project config
            this.donation.transactionType = this.optionService.getTransactionType();
        }

        // add fields for the donator data if they should be visible
        this.showDonatorData = this.optionService.getOption('has_show_donator_data') === true;
        if(this.showDonatorData === true || this.projectType == 'event') {
            this.donation.donatorData = new DonatorData();
        }

        // default value for the donation amount
        this.donation.amount = this.optionService.getOption("donation_value_default");

        // read the start value from the URL
        const startValueParameter: number = Math.round(+this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_PRESET) * 100) / 100;
        if (startValueParameter != null) {
            // there is a parameter in the URL. Check if this is valid
            const donationValueMin = this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_MIN_PRESET) || this.optionService.getOption("donation_value_min");
            const donationValueMax = this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_MAX_PRESET) || this.optionService.getOption("donation_value_max");

            // set the start value to the value from the URL
            if (donationValueMin <= startValueParameter && donationValueMax >= startValueParameter) {
                this.donation.amount = startValueParameter;
            }
        }

        // init Donation with project data
        this.donation.metaData.projectIdentifier = this.optionService.getIdentifier();
        this.setToken();
        this.donation.metaData.projectId = this.optionService.getProjectId();
        this.donation.metaData.organisationId = this.optionService.getOrganisationId();

        // user data
        this.donation.userLanguage = this.translate.currentLang;

        this.optionService.getUsersCountry().subscribe((res: any) => {
            if(res && res.country) {
                // update user country in donation and contact data
                this.donation.userCountry = res.country;
                this.contact.data.country = res.country;
            }
        }, error => {
            console.warn('Could not get users country, use DE as fallback');
            // ignore error and use DE as default
            this.donation.userCountry = 'DE';
            this.contact.data.country = 'DE';
        });

        /**
         * preselect first donation target
         *
         * REMARK: If you change the presets, you also have to set the values in the coresponding validators/formcontrols
         */
        let targets = this.optionService.getDonationTargets();
        const hasDonationtargetTextfield = this.optionService.getOption('has_donationtarget_textfield');
        // do not preset, if it is disabled and there is more than one target or the single target should be shown has_donationtarget_textfield
        const noTargetPreset = this.optionService.getOption('has_target_no_selection') && (targets.length > 1 || hasDonationtargetTextfield === true);

        if(targets.length > 0) {
            // check if we have some preset value in the URL
            let targetPresetParameter: string = this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET_FIX) || this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET);
            let targetIdPresetParameter: string = this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_ID_PRESET_FIX) || this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_ID_PRESET);
            if(targetPresetParameter || targetIdPresetParameter) {
                // check if the value is available
                for(let value of targets) {
                    if((targetPresetParameter !== null && value.donation_target === targetPresetParameter) || (targetIdPresetParameter !== null && value.internal_id === targetIdPresetParameter)) {
                        this.donation.target = value.donation_target;
                        break;
                    }
                }
            }

            // if we do not have a target yet but we have a target textfield 
            if (!this.donation.target && hasDonationtargetTextfield === true) {
                targetPresetParameter = this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET) || this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET_FIX);
                if(targetPresetParameter) {
                    this.donation.target = targetPresetParameter;
                }
            }

            // check, if we have a fixed value
            if((this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET_FIX) ||
                this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_ID_PRESET_FIX)) && this.donation.target) {
                // reduce the available targets to the selected and force to show it
                const availableTargets = targets.filter(target => target.donation_target === this.donation.target);
                this.optionService.overwriteDonationTargets(availableTargets);
                this.optionService.overwriteOption('has_show_single_target', true);
            }

            // check if there was a preset yet, if not preset the first target (except presetting is forbidden)
            if(!this.donation.target && noTargetPreset !== true) {
                this.donation.target = targets[0].donation_target;
            }
        }
        else if(hasDonationtargetTextfield === true) {
            // donation target should be given as textinput, check if we have a parameter
            let targetPresetParameter: string = this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET) || this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET_FIX);
            if(targetPresetParameter) {
                this.donation.target = targetPresetParameter;
            }
        }

        let donation_rythm = this.optionService.getOption("donation_rhythm");

        // check if we have a preset of the donation rhythm, either in the project option or in the URL
        let rhythmPresetParameter: string = this.optionService.getOption('default_rhythm');
        if(this.urlService.findGetParameter(this.urlService.PARAMETER_RYTHM_PRESET) !== null) {
            rhythmPresetParameter = this.urlService.findGetParameter(this.urlService.PARAMETER_RYTHM_PRESET);
        }

        // we have a selection, check if it is valid
        if(rhythmPresetParameter === "one_time") {
            // one_time rythm is in the preset
            this.donation.rythm = "one_time";
        }
        else if(donation_rythm[rhythmPresetParameter] === true) {
            // a recurring rhythm is in the preset
            this.donation.recurringRythm = rhythmPresetParameter;
            this.donation.rythm = "recurring";
        }
        else {
            // no preset yet, select the first available option
            if (donation_rythm.one_time) {
                this.donation.rythm = "one_time";
            }
            else {
                // no one_time rythm, set the first recurring rhytm
                for (let rhythm of RythmTargetComponent.allRecuringDonationRhythms) {
                    if (donation_rythm[rhythm] === true) {
                        this.donation.recurringRythm = rhythm;
                        this.donation.rythm = "recurring";
                        break;
                    }
                }
            }
        }

        // save the trigger
        if (__twingle_trigger) {
            this.donation.trigger = __twingle_trigger;
        }

        // save the campaign
        const campaign: string = this.urlService.findGetParameter(this.urlService.PARAMETER_CAMPAIGN);
        if (campaign !== null) {
            this.donation.metaData.campaign = campaign;
        }

        // preset remarks
        const remarks: string = this.urlService.findGetParameter(this.urlService.PARAMETER_REMARKS);
        if (remarks !== null) {
          this.donation.remarks = remarks;
        }
    }

    /**
     *
     * @param newStatus
     */
    onStatusChange(newStatus: string): void {
      if (newStatus === StatusComponent.OK && this.donationStatus !== StatusComponent.OK) {
        // get new token
        this.setToken();
      }
    }

    /**
     * Get a transaction token from the server and set it on the donation.
     *
     * @param retry
     * @protected
     */
    protected setToken(retry: number = 0): void {
      this.optionService.getToken().subscribe((res: any) => {
        if (res && res.token) {
          this.donation.metaData.token = res.token;
        }
      }, error => {
        // retry more times with some timeout
        if (retry < DonationComponent.TOKEN_RETRY_COUNT) {
          retry++;
          setTimeout(() => this.setToken(retry), DonationComponent.TOKEN_RETRY_TIMEOUT);
        }
        else {
          throw new Error('Could not get token from server. Error: ' + JSON.stringify(error));
        }
      });
    }

    /**
     * method is called after the component has initialized with all its content
     */
    public ngAfterViewInit(): void {
        // bind eventhandler for first focus of an input element
        const elements = document.querySelectorAll(DonationComponent.VISIT_SELECTOR);
        for (let i =  0; i < elements.length; i++) {
            elements[i].addEventListener("focus", () => {
                this.optionService.trackVisit(this.donation);
                TrackingComponent.visit();
            }, false);
        }

        // check the screen size again
        this.resizeWindow(null);

        // if embedded, observe the resize.
        if (this.isEmbedded()) {
          this.initResizeObersver();
        }

        // add class to body so that the loading is finished
        this.renderer.addClass(document.body, 'donation-app-loaded');
    }

    /**
     * Initialize a resize observer for fast height detection
     */
    initResizeObersver(): void {
      const elementToObserve = document.getElementById("twingleBodyWrapper");
      if (!elementToObserve) {
        // stop here, element missing
        return;
      }

      if (!window.ResizeObserver) {
        // browser does not support detection, fallback to interval
        setInterval(() => {
          let height = elementToObserve.offsetHeight;
          const infotext = document.querySelector("#payment_method_debit p.infotext");
          if (infotext && infotext.clientHeight > 0) {
            // there is an infotext shown, put some more height on the system
            height += 160;
          }
          if (height !== this.bodyHeight) {
            this.bodyHeight = height;
            parent.postMessage({
              "type": "size",
              "value": this.bodyHeight,
              "cid": OptionService.getConatinerId()
            }, "*");
          }
        }, 500);

        return;
      }

      // default: use ResizeObserver
      const resizeObserver = new ResizeObserver(entries => {
        if (entries.length > 0) {
          const height = entries[0].contentRect.height
          // send a message if the height changes
          if (height !== this.bodyHeight) {
            this.bodyHeight = height;
            parent.postMessage({
              "type": "size",
              "value": this.bodyHeight,
              "cid": OptionService.getConatinerId()
            }, "*");
          }
        }
      });

      resizeObserver.observe(elementToObserve);
    }

    /**
     * Set all toggles to act like an in-app-browser.
     */
    setInAppTarget(): void {
        this.paymentFormTarget = '_top';
    }

    /**
     * onInit of the component
     */
    public ngOnInit(): any {
        // check if the contact data should be requested manually
        this.contactMandatory = ContactComponent.isContactMandatory();

        // how are redirect forms send, new window or top frame?
        if (this.isInAppBrowser() === true || this.urlService.findGetParameter(this.urlService.PARAMETER_APP) === "1") {
            this.setInAppTarget();
        }

        // Init form empty FormGroup, the Controls have to added in the single Components
        if (this.version == "form") {
            this.pageForm = new FormGroup({});
        }
        else {
            // widget
            this.widgetPaymentForm = new FormGroup({});
            this.widgetStartForm = new FormGroup({});
        }
        this.contactForm = new FormGroup({});

        // show/hide the stepindex
        this.hasStepIndex = this.optionService.getOption('has_step_index') === true;

        // save the url on which the transaction was started
        this.donation.url = document.location.href;

        // should we show the header on the widget version?
        this.forceWidgetTeaser = this.optionService.getOption('has_widget_teaser');

        // do we have a thankyou text instead of the widget header?
        this.showPageheaderRedirectText = this.urlService.findGetParameter(this.urlService.PARAMETER_THANKYOU) === '1';

        if (this.isEmbedded()) {
            const that = this;

            // receive the outer url.
            const receiveURL: any = function(event: any) {
                if (event.data && event.data.type && event.data.type == "seturl") {
                    that.donation.url = event.data.value;
                }
            };
            // bind message receiver
            if (window.addEventListener){
                window.addEventListener("message", receiveURL, false);
            }
            else {
                attachEvent("onmessage", receiveURL);
            }
            // request for the URL to the parent frame
            parent.postMessage({
                "type": "geturl",
                "value": null,
                "cid": OptionService.getConatinerId()
            }, "*");
        }

        // submit the form if the express checkout element is clicked.
        this.stripeService.onExpressCheckoutSubmit.subscribe(data => {
          if (data && data.email) {
            this.donation.userEmail = data.email;
            this.contact.data.firstname = data.firstname ?? null;
            this.contact.data.lastname = data.lastname ?? null;
          }
          this.submitForm();
        });

        // show error message if there was an error on stripe loading
        this.stripeService.onStripeError.subscribe(error => {
          if (error) {
            this.donationStatus = StatusComponent.ERROR;
            this.donationStatusMessage = 'payment.stripe.error.' + error;
          }
        });

        // Init the resize function
        this.resizeWindow(null);

        // For Development only, jump to a specific step
        // this.donation.paymentMethod = 'konfipay';
        // this.donation.paymentBanktransfer.receiverName = "Beispiel";
        // this.donation.paymentBanktransfer.receiverIban = "DE12 3456 2348 39393 3933";
        // this.donation.paymentBanktransfer.receiverBic = "BYLJFDHJHD";
        // this.donation.paymentBanktransfer.paymentReference = "SHSHSHS";
        // this.contact.wants_newsletter = true;
        // this.setStep("thankyou");
    }

    /**
     * reloads the widget (url)
     */
    reloadWidget(): void {
        document.location.reload();
    }

    /**
     * checks if the widget is embeded or not.
     */
    isEmbedded(): boolean {
        return window.parent && window !== window.parent;
    }

    /**
     * Sets a new step
     * @param newStep
     * @param scroll if true, the page tries to scroll upwards
     */
    setStep(newStep: string, scroll: boolean = false): void {
        TrackingComponent.stepChanged(newStep);
        this.step = newStep;

        if (scroll === true) {
            const elementsToScrollTo = document.querySelectorAll("body");

            // check if we are inside an iframe
            if (this.isEmbedded()) {
                // check if we need some offset when scrolling
                setTimeout(() => {
                    let offset = 0;

                    // scroll to the step navigation of the first label
                    if (elementsToScrollTo && elementsToScrollTo.length > 0) {
                        offset = elementsToScrollTo[0].getBoundingClientRect().top - 80;
                    }
                    // if there is an event header, hat this also to the offset
                    const eventHeader = document.querySelectorAll('event-header');
                    if (eventHeader.length > 0) {
                        offset += eventHeader[0].getBoundingClientRect().height;
                    }

                    // send to the parent frame to scroll upwards
                    parent.postMessage({
                        "type": "scrollToTop",
                        "value": offset,
                        "cid": OptionService.getConatinerId()
                    }, "*");
                }, 0);
            }
            else {
                // we are on a donation page, we should scroll up there
                if (elementsToScrollTo[0] &&
                    typeof elementsToScrollTo[0].scrollIntoView !== "undefined") {
                    elementsToScrollTo[0].scrollIntoView(true);
                }
            }
        }
    }

    /**
     * function that is triggered every time a message is received from outside
     * @param event
     */
    @HostListener('window:message', ['$event'])
    messageReceiver(event: any): void {
        if (event.data && event.data.result) {
            // reset donation info message
            let message = null;

            // do something based on the result
            switch (event.data.result) {
                case 1:
                    const paymentMethod = this.donation.paymentMethod;

                    // if stripe (google pay, credit card), confirm the payment element.
                    if (paymentMethod === 'stripe_creditcard' || paymentMethod === 'stripe_google_pay') {
                      const stripeData = JSON.parse(event.data.message);
                      this.stripeService.confirm(stripeData.clientSecret, stripeData.returnUrl).then(result => {
                        if (result.error) {
                          this.errortransaction(result.error);
                        }
                        else {
                          this.finishTransaction(event);
                        }
                      }, error => {
                        this.errortransaction(error);
                        console.log("stripe_error", error);
                      });

                      return;
                    }

                    this.finishTransaction(event);

                    return;
                case 2:
                    // donation canceled
                    console.log("Donation canceled");
                    TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_CANCELED, this.donation);
                    // set status
                    this.donationStatus = StatusComponent.CANCELED;

                    // get new token
                    this.setToken();

                    // change the step
                    if (this.size === "small" && this.version !== 'form') {
                        this.setStep("payment-select");
                    }
                    else if (this.version === "shop" || this.version === "giftshop") {
                      this.setStep("payment");
                    }
                    else {
                        this.setStep("start");
                    }
                    return;
                case 3:
                    // error in donation
                    console.log("Donation invalid");
                    TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_ERROR, this.donation);

                    // reset error message
                    this.donationStatus = StatusComponent.ERROR;
                    this.donationStatusMessage = null;

                    // get new token
                    this.setToken();

                    try {
                        message = typeof event.data.message === "object" ? event.data.message : JSON.parse(event.data.message);
                    } catch (e) {
                        console.error("invalid json", event.data.message);
                        message = null;
                    }

                    if (message && message.error_code) {
                        // specific translation available
                        if (message.error_code === 'wrongmethod') {
                          this.donationStatusMessage = "payment.error.newwindow";
                        }
                        else if (message.error_message === 'validation_failed') {
                          // validationerror, try to get specific error message or return default error message
                          this.donationStatusMessage = "status.error.validation." + message.error_code;
                          this.translate.get(this.donationStatusMessage).subscribe((res: string) => {
                            if (res === this.donationStatusMessage) {
                              // no specific error message available
                              this.donationStatusMessage = "status.error.validation";
                            }
                          }, () => this.donationStatusMessage = "status.error.validation");
                        }
                        else {
                          this.donationStatusMessage = "payment." + this.donation.paymentMethod + ".error." + message.error_code;
                          this.translate.get(this.donationStatusMessage).subscribe((res: string) => {
                                if (res === this.donationStatusMessage) {
                                    // if no specific translation is there, try the default one
                                    this.donationStatusMessage = "payment." + this.donation.paymentMethod + ".error.default";
                                    this.translate.get(this.donationStatusMessage).subscribe((res2: string) => {
                                        if (res2 === this.donationStatusMessage) {
                                            this.donationStatusMessage = null;
                                            this.scrollToError();
                                        }
                                    });
                                }
                            });
                        }
                        this.scrollToError();
                    }

                    // change the step
                    if (this.size === "small") {
                        this.setStep("payment-select");
                    }
                    else if (this.version === "shop" || this.version === "giftshop") {
                        this.setStep("payment");
                    }
                    else {
                        this.setStep("start");
                    }
                    // update the component because the status has changed
                    this.changeDetector.detectChanges();

                    return;
                case 4:
                    // redirect
                    TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_REDIRECT, this.donation);
                    this.donationStatus = StatusComponent.REDIRECT;
                    this.donation.paymentUrl = event.data.message;
                    return;
                case 5:
                    // iframe, next step
                    TrackingComponent.donationStatusChanged( TrackingComponent.DONATION_STATUS_IFRAME, this.donation);
                    this.donation.paymentUrl = event.data.message;
                    this.donationStatus = StatusComponent.IFRAME;
                    this.setStep("iframe");
                    return;
                default:
                    this.donationStatus = StatusComponent.ERROR;
                    return;
            }
        }
    }

    /**
     * Handle erros on donation initialization.
     *
     * @param error
     */
    errortransaction(error: any): void {
      TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_ERROR, this.donation);

      // reset error message
      this.donationStatus = StatusComponent.ERROR;
      this.donationStatusMessage = error.message ?? null;

      // reset token
      this.setToken();

      this.scrollToError();
    }

    /**
     * scrolls to the error message of the donation.
     */
    scrollToError(): void {
      // try to scroll to the error message
      setTimeout(() => {
        const warning = document.querySelectorAll('.status.warning');
        if (warning.length > 0) {
          this.elementService.scrollToElement(warning[0] as HTMLElement);
        }
      }, 0);
    }

    /**
     * finish transaction ans show thankyou page-
     * @param event
     */
    finishTransaction(event: any): void {
      let message = null;
      this.donationStatus = StatusComponent.OK;

      if (event.data.transaction_guid) {
        this.donation.transactionGuid = event.data.transaction_guid;
      }

      // donation successful
      TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_SUCCESS, this.donation);

      // check if there is a message
      if (event.data.message.length > 0) {
        try {
          message = JSON.parse(event.data.message);
        } catch (e) {
          console.error("invalid json", event.data.message);
          message = null;
        }
      }
      console.log("Donation successful", message);

      // banktransfer
      if (message && this.donation.paymentMethod === 'banktransfer') {
        // we get the account infos back from the backend
        this.donation.paymentBanktransfer.receiverName = message.account_holder;
        this.donation.paymentBanktransfer.receiverIban = message.iban;
        this.donation.paymentBanktransfer.receiverBic = message.bic;
        this.donation.paymentBanktransfer.paymentReference = message.payment_reference;
        this.donation.paymentBanktransfer.paymentReferenceMessage = message.payment_reference_message;
      }

      // everything that returns contact data (i.e. PayPal)
      if (message && message.contact_data) {
        this.contact.data.postal_code = this.contact.data.postal_code ?? message.contact_data.zip_code;
        this.contact.data.city = this.contact.data.city ?? message.contact_data.city;
        this.contact.data.street = this.contact.data.street ?? message.contact_data.street;
        this.contact.data.firstname = this.contact.data.firstname ?? message.contact_data.firstname;
        this.contact.data.lastname = this.contact.data.lastname ?? message.contact_data.lastname;
      }

      // Show the contact step.
      this.setStep("thankyou", true);
    }

    /**
     * Starts a donation with the current input of the user
     *
     * @returns {boolean}
     */
    startDonation(): any {
        if (this.donationStatus === StatusComponent.WAITING || this.donationStatus === StatusComponent.REDIRECT) {
          // do not start the donmation again
          return;
        }

        if (this.donationTokenUsed === true) {
          console.warn('Donation token was already used.');
        }

        this.donationTokenUsed = true;

        // set status to waiting
        this.donationStatus = StatusComponent.WAITING;
        this.donationStatusMessage = null;
        TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_START, this.donation);

        // if payment method is stripe, submit stripe form first.
        if (this.donation.paymentMethod === 'stripe_creditcard') {
          this.stripeService.submit().then((result) => {
            if (result.error) {
              this.errortransaction(result.error);
            }
            else {
              // start donation with backend
              this.donationService.startDonation(this.donation, this.contact);
            }
          }, error => {
            // an error occured, show error message.
            TrackingComponent.donationStatusChanged(TrackingComponent.DONATION_STATUS_ERROR, this.donation);

            // reset error message
            this.donationStatus = StatusComponent.ERROR;
            this.donationStatusMessage = error.message ?? null;
          });

          // stop here
          return;
        }


        // only start donation if the paymentMethod is not in the list of redirect methods
        if (PaymentComponent.redirectPaymentMethods.indexOf(this.donation.paymentMethod) === -1) {
            this.donationService.startDonation(this.donation, this.contact);
        }
        else {
            // we have a redirect. Check if we got the redirect status info
            setTimeout(() => {
                if (this.donationStatus === StatusComponent.WAITING) {
                    // we are still waiting, the popup might not be opend, use the current window.
                    this.donationStatus = StatusComponent.ERROR;
                    this.setInAppTarget();
                    this.donationStatusMessage = 'payment.error.noredirect';
                }
            }, DonationComponent.CHECK_REDIRECT_STATUS_SECONDS * 1000);
        }

        return false;
    }

    /**
     * is called when a input form is submitted (it is valid in this case)
     */
    submitForm(): void {
        let form = new FormGroup({});
        let form2: FormGroup = null;

        // Define which form has to be validatet, depending on the step/version
        if (this.version === "form") {
            form = this.pageForm;

            if (this.step === "start" && this.contactMandatory === true) {
                form2 = this.contactForm;
            }
        }
        else {
            if (this.step === "start") {
                form = this.widgetStartForm;
            }
            else if (this.step === "contact-mandatory") {
                form = this.contactForm;
            }
            else {
                form = this.widgetPaymentForm;
            }
        }


        if (form.valid === false || (form2 != null && form2.valid === false)) {

            const invalid = []
            for (const name in form.controls) {
                if ((<FormControl>form.controls[name]).invalid) {
                    invalid.push(name);
                }
            }
            console.warn("Form is NOT valid", form, invalid);

            // Set all controls as touched to show the error messages
            for (const name in form.controls) {
                (<FormControl>form.controls[name]).markAsTouched({ onlySelf: true });
            }
            if (form2 != null) {
                // we have a second form that we also want to validate
                for(const name in form2.controls) {
                    (<FormControl>form2.controls[name]).markAsTouched({ onlySelf: true });
                }
            }

            // scroll to the first error element
            setTimeout(() => this.elementService.scrollToElement(document.querySelector('label.error') ?? document.querySelector('.error')), 0);
            return;
        }

        switch (this.version) {
            /**
             * VERSION = page, widget
             */
            case "page":
            case "widget":
            case "shop":
            case "giftshop":
                switch (this.step) {
                    case "start":
                        // only scroll upwards if we are NOT on a event project because they have very high header
                        if (this.contactMandatory === true) {
                            this.setStep("contact-mandatory", this.projectType !== 'event');
                        }
                        else if (this.size === "small" || this.version === "widget") {
                            // next step: payment
                            this.setStep((this.donation.isAmountZero() ? "payment" : "payment-select"), this.projectType !== 'event');
                        }
                        else {
                            // on medium and large, start the validation directly
                            this.startDonation();
                        }
                        break;
                    case "contact-mandatory":
                        if (this.version === "widget" || this.version === "page") {
                            // next step: payment selection
                            this.setStep("payment-select", true);
                        }
                        else if (this.version === "shop" || this.version === "giftshop") {
                            // next step: payment or payment select
                            this.setStep(this.size === "small" && this.donation.isAmountZero() === false ? "payment-select" : "payment", true);
                        }
                        else {
                            // on medium and large, start the validation directly
                            this.startDonation();
                        }
                        break;
                    case "payment-select":
                        this.setStep("payment", true);
                        break;
                    case "payment":
                        this.startDonation();
                        break;
                    default:
                        console.debug("Implement this (submit form after donation on widget)!");
                }
                break;
            /**
             * VERSION = form
             */
            case "form":
                switch (this.step) {
                    case "start":
                        this.startDonation();
                        break;
                    default:
                        console.debug("Implement this (submit form after donation on page, form)!");
                }
                break;
            /**
             * VERSION = unknown => ERROR
             */
            default:
                console.error("unknown version");
        }
    }

    /**
     * toggle for showing/hiding the widget headline. Is triggered by an event of the HeadlineComponent
     */
    toggleWidgetHeadline(value: any): void {
        this.showWidgetHeadline = value;
    }

    /**
     * Returns a list of placeholders that we need inside the last submit button in the widget
     */
    getSubmitButtonPlaceholders(): any {
        // get the textkey for the rythm placeholder
        let rythmPlaceholderTextkey = 'rythm.one_time.placeholder';
        if (this.donation.recurringRythm) {
            rythmPlaceholderTextkey = 'rythm.recurring.' + this.donation.recurringRythm + '.placeholder';
        }

        // if we have a membership widget, we calculate the value, based on the selected rythm
        let membershipAmount = this.donation.amount;
        let membershipExtraAmount = this.donation.extraAmount;
        const membershipPercent = this.optionService.getOption('extra_amount_unit') === 'percent';
        if (this.donation.recurringRythm) {
            switch (this.donation.recurringRythm) {
                case 'yearly':
                    membershipAmount *= 12;
                    if (membershipPercent) {
                      membershipExtraAmount *= 12;
                    }
                    break;
                case 'halfyearly':
                    membershipAmount *= 6;
                    if (membershipPercent) {
                      membershipExtraAmount *= 6;
                    }
                    break;
                case 'quarterly':
                    membershipAmount *= 3;
                    if (membershipPercent) {
                      membershipExtraAmount *= 3;
                    }
                    break;
            }
        }

        // format amounts
        let amount: string = this.donation.amount + '';
        let membershipAmountPlaceholder = membershipAmount + '';
        try {
            const hasDecimals = this.donation.amount % 1 !== 0;
            amount = this.decimalPipe.transform(isNaN(this.donation.amount) ? parseFloat(this.donation.amount + '') : this.donation.amount, hasDecimals ? '1.2-2' : '1.0-0');

            membershipAmountPlaceholder = this.decimalPipe.transform(isNaN(membershipAmount) ? parseFloat(membershipAmount + '') : membershipAmount, '1.2-2');
        } catch (e) {
            // ignore errors from the pipe because we have the fallback already
        }

        // add extra amount if selected
        if (this.donation.extraAmount > 0) {
            const hasDecimals = this.donation.extraAmount % 1 !== 0;
            amount += ' + ' + this.decimalPipe.transform(isNaN(this.donation.extraAmount) ? parseFloat(this.donation.extraAmount + '') :
              this.donation.extraAmount, hasDecimals ? '1.2-2' : '1.0-0');
            membershipAmountPlaceholder += ' + ' + this.decimalPipe.transform(isNaN(membershipExtraAmount) ? parseFloat(membershipExtraAmount + '') :
              membershipExtraAmount, hasDecimals ? '1.2-2' : '1.0-0');
        }

        return {
            amount: amount ?? '0',
            membershipAmount: membershipAmountPlaceholder ?? '0',
            currencySymbol: this.donation.currencySymbol,
            rythm: this.translate.instant(rythmPlaceholderTextkey)
        };
    }

    /**
     * Complete list of steps for the widget where the stepindex is visible and the order of the steps
     */
    getStepsForIndex(): any {
        let menu = {'start': []};
        if(this.contactMandatory === true) {
            menu['contact-mandatory'] = ['contact-mandatory-finished'];
        }
        if(this.version === 'widget' || this.size === 'small' || this.donation.paymentMethod === 'betterpayment_creditcard' || this.contactMandatory) {
            menu['payment'] = ['payment-select', 'iframe'];
        }

        // end pages
        if(this.contactMandatory === false && ['thankyou', 'banktransfer', 'end', 'contact'].indexOf(this.step) !== -1) {
            // case 1: contact ist NOT mandatory and we have the donation already => show address step as extra item
            menu['thankyou'] = ['banktransfer'];
            menu['contact'] = ['banktransfer', 'end'];
        }
        else {
            // case 2: Not case 1
            menu['thankyou'] = ['contact', 'banktransfer', 'end'];
        }

        return menu;
    }

    /**
     * Checks if we are inside a app browser (instagram, facebook etc)
     * @returns {boolean}
     */
    isInAppBrowser(): boolean {
        const ua = navigator.userAgent || navigator.vendor || null;
        const userAgents = ["FBAN", "FBAV", "Instagram", "ecosia ios", "linkedinapp", "DuckDuckGo", "TikTok", "Snapchat", "MicroMessenger", "Twitter", "Pinterest", "musical_ly"];
        return userAgents.findIndex(userAgent => ua.toLowerCase().indexOf(userAgent.toLowerCase()) > -1) > -1;
    }
}
